/**
 * Notification Service
 *
 * Provides reactive notification + meta state and a fetchNotification API.
 * Wraps MarketingHttpClient to request `/notifications.json`.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-12-03
 */

import {
  AxiosError,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { MarketingHttpClient, NotificationHttpClient } from "@/lib/repositories";
import { ref } from "vue";
import { useUserStore } from "@/lib/stores";

/** --------------------------------------------------
 * Interfaces
 * -------------------------------------------------- */
export interface Notification {
  id: number;
  show_download: boolean;
  message: string;
  status: number;
  is_unread: boolean;
  [key: string]: unknown;
}

export interface NotificationApiResponse {
  notifications: Notification[];
  meta?: Record<string, unknown>;
}

/** --------------------------------------------------
 * Notification Service (SINGLETON)
 * The service is instantiated once and exported as `useNotificationService`.
 * This ensures that state (notifications + meta) is preserved across components
 * WITHOUT requiring multiple API calls.
 * -------------------------------------------------- */
const notificationService = () => {
  const httpClient = MarketingHttpClient;
  const notificationHttpClient = NotificationHttpClient;

  const RESOURCE = `/notifications.json`;

  // Reactive State
  const data = {
    notifications: ref<Notification[]>([]),
    meta: ref<Record<string, unknown>>({}),
  };

  /**
   * Fetch notifications from a single source
   * @param source The source to fetch from
   * @param client The client to use
   * @param requestConfig The request config
   * @returns The notifications and unread count
   */
  const fetchFromSource = async (
    source: "marketing" | "commerce",
    client: typeof httpClient | typeof notificationHttpClient,
    requestConfig: Record<string, unknown>
  ): Promise<{ notifications: Notification[]; unread_count: number } | null> => {
    try {
      const res = await client.get<NotificationApiResponse>(RESOURCE, requestConfig);
      const unread = Number(res?.data?.meta?.unread_count) || 0;
      const list = res?.data?.notifications ?? [];
      return { notifications: list, unread_count: unread };
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.warn(`[NotificationService] ${source} notifications failed:`, error.message, error.response?.status);
      } else {
        console.warn(`[NotificationService] ${source} notifications failed:`, error);
      }
      return null;
    }
  };

  /**
   * Fetch notifications from marketing and/or commerce based on user access.
   * Each source is fetched independently.
   * Returns the notifications and unread count.
   * @param config Optional Axios config (pagination etc.)
   * @returns The notifications and unread count
   */
  async function fetchNotifications(
    config: Record<string, unknown> = { params: { page: 1, per_page: 10 } }
  ): Promise<{ notifications: Notification[]; unread_count: number }> {
    const userStore = useUserStore();
    const sourceFetchers = [
      {
        allowed: userStore.getProductAccess("marketing") && userStore.getProductAccessOnRoles("marketing"),
        fetch: () => fetchFromSource("marketing", httpClient, config),
      },
      {
        allowed: userStore.getProductAccess("commerce"),
        fetch: () => fetchFromSource("commerce", notificationHttpClient, config),
      },
    ]
      .filter((source) => source.allowed)
      .map((source) => source.fetch()) as Promise<{ notifications: Notification[]; unread_count: number } | null>[];

    const results = await Promise.all(sourceFetchers);
    const meta = { unread_count: 0 };
    const notifications = results.flatMap((notification) => {
      if (!notification) return [];
      meta.unread_count += notification.unread_count;
      return notification.notifications;
    });

    data.notifications.value = notifications;
    data.meta.value = meta;

    return { notifications, unread_count: meta.unread_count };
  }

  return {
    fetchNotifications,
    ...data,
  };
};

/**
 * Exported SINGLETON instance.
 * Using this ensures the same reactive state is shared everywhere.
 */
export const useNotificationService = notificationService();
